import React, { useEffect, useState } from "react";
import "./FileUploadComponent.css";
import { useDispatch, useSelector } from "react-redux";
import { FileUpload } from "primereact/fileupload";
import { ProgressSpinner } from "primereact/progressspinner"; // <--- Esempio di loader PrimeReact
import { allPut, getFiles, uploadFile } from "../Redux/Actions.tsx";
import { AppDispatch, RootState } from "../Redux/Store/index.ts";
import { useAlert } from "./Alert.tsx";
import { Container } from "react-bootstrap";
import FileManager from "./FileManager.tsx";
import { urlcontratti } from "../lib/api.ts";
import { Contratti, FileData } from "../lib/interface.ts";

interface FileUploadComponentProps {
  folderKey: string;
  filePrincipale?: boolean;
  elementContratto?: Contratti;
  folderType: string;
  onFilesLoaded?: (files: any[]) => void;
  onPrincipalFileUpdated?: () => void;
}

const FileUploadComponent: React.FC<FileUploadComponentProps> = ({
  folderKey,
  folderType,
  elementContratto,
  onFilesLoaded,
  onPrincipalFileUpdated,
  filePrincipale = false,
}) => {
  const dispatch: AppDispatch = useDispatch();
  const showAlert = useAlert();
  const { token, profilo } = useSelector((state: RootState) => state.login);

  // -- Stati locali
  const [fileList, setFileList] = useState<FileData[]>([]);
  const [isFileManagerOpen, setIsFileManagerOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState<File | null>(null);
  const [customFolder, setCustomFolder] = useState<string | null>(null);
  const [principalFileName, setPrincipalFileName] = useState("");
  const [isLoadingPrincipalFile, setIsLoadingPrincipalFile] = useState(true);

  // <--- Nuovo stato per loader
  const [isUploading, setIsUploading] = useState(false);

  // Gestione dell'upload:
  const handleFileUpload = (event: any) => {
    const files: File[] = event.files;
    if (files.length > 0) {
      setPreviewFile(files[0]);
      setIsFileManagerOpen(true);
    }
  };

  // Conferma effettiva dell'upload
  const confirmUpload = (isPrincipal: boolean) => {
    if (!previewFile) return;

    // Mostriamo lo spinner
    setIsUploading(true);

    // Chiamiamo la nostra azione Redux
    dispatch(
      uploadFile(
        token,
        previewFile,
        profilo.id,
        folderKey,
        folderType,
        isPrincipal,
        showAlert,
        customFolder || undefined
      )
    ).then((result) => {
      // Qualsiasi sia l'esito, nascondiamo lo spinner
      setIsUploading(false);
      setPreviewFile(null);

      // Se è andato tutto bene, aggiorniamo la lista
      if (result) {
        fetchFiles();
        // Se il file è principale e abbiamo un elemento Contratto, facciamo la PUT
        if (result.fileKey && elementContratto && isPrincipal) {
          const contrattoFilePrincipale = {
            ...elementContratto,
            documentoPrincipale: result.fileKey,
          };
          dispatch(
            allPut(
              urlcontratti + "/" + elementContratto.id,
              token,
              contrattoFilePrincipale
            )
          ).then((res) => {
            if (res && onPrincipalFileUpdated) {
              onPrincipalFileUpdated();
            }
          });
        }
      }
    });
  };

  // Funzione di fetch
  const fetchFiles = async (): Promise<FileData[]> => {
    const finalFolderKey = customFolder
      ? `${folderKey}/${customFolder}`
      : folderKey;

    try {
      const files = await dispatch(getFiles(token, finalFolderKey, folderType));
      if (files) {
        setFileList(files);
        onFilesLoaded && onFilesLoaded(files);
      }
      return files || [];
    } catch (error) {
      console.error("Errore durante il fetch dei file:", error);
      return [];
    }
  };

  useEffect(() => {
    const loadPrincipalFile = async () => {
      if (folderKey && folderType) {
        try {
          setIsLoadingPrincipalFile(true);
          const files = await fetchFiles();

          if (elementContratto?.documentoPrincipale && files?.length > 0) {
            const principalFile = files.find(
              (f) => f.id === elementContratto.documentoPrincipale
            );
            setPrincipalFileName(
              principalFile ? principalFile.fileKey.split("/").pop() || "" : ""
            );
          } else {
            setPrincipalFileName("");
          }
        } catch (error) {
          setPrincipalFileName("");
        } finally {
          setIsLoadingPrincipalFile(false);
        }
      }
    };

    loadPrincipalFile();
    // Dipendenze ridotte al minimo
  }, [folderKey, folderType, customFolder]);

  return (
    <>
      <div className="d-flex justify-content-between">
        <h5 className="text-start m-0">File</h5>
        <p
          className="d-flex text-center align-items-center"
          style={{ cursor: "pointer" }}
          onClick={() => setIsFileManagerOpen(true)}
        >
          Visualizza di più
          <i className="pi pi-angle-right"></i>
        </p>
      </div>

      <Container className="custom-file-upload-container">
        <FileUpload
          mode="advanced"
          name="file"
          customUpload
          auto
          uploadHandler={handleFileUpload}
          chooseLabel="Carica file"
          style={{ backgroundColor: "#ffe7ae", border: "none" }}
          accept="image/*,.pdf,.doc,.docx"
          className="custom-file-upload"
          emptyTemplate={
            <div className="upload-area">
              <i className="pi pi-cloud-upload upload-icon"></i>
              <p>Trascina o seleziona un file</p>
            </div>
          }
        />
      </Container>

      {/* Spinner di caricamento (puoi sostituirlo con un overlay più complesso) */}
      {isUploading && (
        <div className="uploading-overlay">
          <ProgressSpinner />
          <p>Caricamento in corso...</p>
        </div>
      )}

      {!isLoadingPrincipalFile && isFileManagerOpen && (
        <FileManager
          folderKey={folderKey}
          folderType={folderType}
          setPreviewFile={setPreviewFile}
          fileList={fileList}
          previewFile={previewFile}
          customFolder={customFolder}
          setCustomFolder={setCustomFolder}
          onConfirmUpload={confirmUpload}
          principalFileName={principalFileName}
          onClose={() => {
            setIsFileManagerOpen(false);
            setPreviewFile(null);
          }}
          onDeleteSuccess={fetchFiles}
          elementContratto={elementContratto}
          fetchFiles={fetchFiles}
        />
      )}
    </>
  );
};

export default FileUploadComponent;
